import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from '@components/molecules/Modal';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { Input } from '@components/atoms/Input';
import { InputCurrency } from '@components/atoms/InputCurrency';
import { Checkbox } from '@components/atoms/Checkbox';
import { Button } from '@components/atoms/Button';
import { WarehouseSelector, WarehouseOption } from '@components/molecules/WarehouseSelector';
import { MaterialSelector, MaterialOption } from '@components/molecules/MaterialSelector';
import { StockLevelIndicator } from '@components/molecules/StockLevelIndicator';
import {
  useWarehouses,
  useStockLevels,
  useMaterialCatalog,
  useCreateTaskMaterialFromWarehouse,
} from '@hooks/api/warehouse';
import {
  buildAvailabilityIndex,
  describeStockElsewhere,
  getCatalogAvailability,
  suggestWarehouseId,
} from '@utils/stockAvailability';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

interface AddWarehouseMaterialModalProps {
  isOpen: boolean;
  onClose: () => void;
  taskId: string;
  isDelegated?: boolean;
  onSuccess?: () => void;
}

/**
 * Material-first picker.
 *
 * The previous warehouse-first flow defaulted to the org's default warehouse and
 * scoped stock to it, so a material stored anywhere else read as "Out of Stock".
 * Here the user picks the material against org-wide availability, then picks
 * which warehouse to pull it from — annotated with what each one actually holds.
 */
export const AddWarehouseMaterialModal: React.FC<AddWarehouseMaterialModalProps> = ({
  isOpen,
  onClose,
  taskId,
  isDelegated = false,
  onSuccess,
}) => {
  const [selectedMaterial, setSelectedMaterial] = useState<MaterialOption | null>(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState<WarehouseOption | null>(null);
  const [quantity, setQuantity] = useState('');
  const [pricePerUnit, setPricePerUnit] = useState<number | null>(null);
  const [chargeToClient, setChargeToClient] = useState(false);

  // Data hooks — stock levels are fetched org-wide (no warehouse filter) so the
  // material list reflects every warehouse, not just the default one.
  const { data: warehouses = [], isLoading: warehousesLoading } = useWarehouses();
  const { data: materials = [], isLoading: materialsLoading } = useMaterialCatalog();
  const { data: stockLevels = [], isLoading: stockLoading } = useStockLevels();

  const availabilityIndex = useMemo(
    () => buildAvailabilityIndex(stockLevels, warehouses),
    [stockLevels, warehouses],
  );

  const materialAvailability = useMemo(
    () =>
      selectedMaterial
        ? getCatalogAvailability(availabilityIndex, selectedMaterial.material.catalogId)
        : undefined,
    [availabilityIndex, selectedMaterial],
  );

  // Mutation
  const createMaterial = useCreateTaskMaterialFromWarehouse();

  // Reset form on close
  useEffect(() => {
    if (!isOpen) {
      setSelectedMaterial(null);
      setSelectedWarehouse(null);
      setQuantity('');
      setPricePerUnit(null);
      setChargeToClient(false);
    }
  }, [isOpen]);

  // Suggest the warehouse holding the most of the chosen material (falling back
  // to the org default) — the user can still override it.
  useEffect(() => {
    if (!selectedMaterial || !materialAvailability) {
      setSelectedWarehouse(null);
      return;
    }

    const suggestedId = suggestWarehouseId(materialAvailability, warehouses);
    const suggested = warehouses.find((w) => w.warehouseId === suggestedId);

    setSelectedWarehouse(
      suggested
        ? { value: suggested.warehouseId, label: suggested.name, warehouse: suggested }
        : null,
    );
    setQuantity('');
  }, [selectedMaterial, materialAvailability, warehouses]);

  // Derived state — availability is scoped to the warehouse being pulled from,
  // since that's what the reservation actually consumes.
  const selectedWarehouseId = selectedWarehouse?.warehouse.warehouseId;
  const warehouseStock = materialAvailability?.byWarehouse.find(
    (w) => w.warehouseId === selectedWarehouseId,
  );
  const quantityNum = parseFloat(quantity) || 0;
  const maxAvailable = warehouseStock?.quantityAvailable || 0;
  const hasStock = maxAvailable > 0;
  const isQuantityPositive = quantityNum > 0;
  const isQuantityWithinStock = quantityNum <= maxAvailable;

  const stockElsewhere = materialAvailability
    ? describeStockElsewhere(materialAvailability, selectedWarehouseId)
    : '';

  // Base form validity (material + warehouse selected + quantity > 0)
  const baseValid =
    selectedWarehouse && selectedMaterial && isQuantityPositive && !createMaterial.isPending;

  // Reserve button: needs stock and quantity within stock
  const canReserve = baseValid && hasStock && isQuantityWithinStock;

  // Planned button: just needs base validity (quantity > 0)
  const canAddPlanned = baseValid;

  // Cost summary
  const unitCost =
    warehouseStock?.stockLevel?.averageCost ||
    selectedMaterial?.material.averageCost ||
    0;
  const totalCost = quantityNum * unitCost;
  const priceNum = pricePerUnit || 0;
  const totalPrice = quantityNum * priceNum;

  const handleSubmit = async (skipReservation: boolean) => {
    if (!selectedWarehouse || !selectedMaterial || !isQuantityPositive) return;

    await createMaterial.mutateAsync({
      taskId,
      catalogId: selectedMaterial.material.catalogId,
      warehouseId: selectedWarehouse.warehouse.warehouseId,
      quantity: quantityNum,
      pricePerUnit: chargeToClient ? priceNum || undefined : undefined,
      chargeToClient,
      skipReservation,
    });

    onSuccess?.();
    onClose();
  };

  // Custom action buttons for the modal footer
  const actionButtons = (
    <>
      <Button
        size="md"
        variant="secondary"
        type="button"
        disabled={!canAddPlanned || createMaterial.isPending}
        onClick={() => handleSubmit(true)}>
        Add as Planned
      </Button>
      {hasStock && (
        <Button
          size="md"
          type="button"
          disabled={!canReserve}
          loading={createMaterial.isPending}
          onClick={() => handleSubmit(false)}>
          Reserve
        </Button>
      )}
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Material from Warehouse"
      actionButtons={actionButtons}
      size="medium">
      <div className="flex flex-col gap-5">
        {/* Material Selection — org-wide stock, so nothing reads as out of stock
            just because it lives in another warehouse */}
        <FormGroup label="Material" htmlFor="material">
          <MaterialSelector
            materials={materials}
            availabilityIndex={availabilityIndex}
            value={selectedMaterial}
            onChange={setSelectedMaterial}
            isLoading={materialsLoading || stockLoading}
            showOutOfStock
            allowOutOfStock
            required
          />
        </FormGroup>

        {/* Warehouse Selection — only once a material is chosen, annotated with
            how much each warehouse actually holds */}
        {selectedMaterial && (
          <FormGroup
            label="Pull from warehouse"
            htmlFor="warehouse">
            <WarehouseSelector
              warehouses={warehouses}
              value={selectedWarehouse}
              onChange={setSelectedWarehouse}
              materialAvailability={materialAvailability}
              isLoading={warehousesLoading}
              required
            />
          </FormGroup>
        )}

        {/* Stock Level Indicator */}
        {selectedMaterial && warehouseStock && (
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <WarehouseIcon className="text-gray-400" sx={{ fontSize: 16 }} />
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                Stock at {selectedWarehouse?.warehouse.name}
              </span>
            </div>
            <StockLevelIndicator
              quantityOnHand={warehouseStock.quantityOnHand}
              quantityReserved={warehouseStock.quantityReserved}
              reorderPoint={selectedMaterial.material.reorderPoint}
              unit={selectedMaterial.material.unit}
              showBar
              showBadge
              showDetails
            />
          </div>
        )}

        {/* No stock in the selected warehouse — say where it actually is */}
        {selectedMaterial && selectedWarehouse && !hasStock && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <WarningAmberIcon className="text-amber-500 mt-0.5" sx={{ fontSize: 18 }} />
            <p className="text-sm text-amber-700">
              {stockElsewhere ? (
                <>
                  No stock in <strong>{selectedWarehouse.warehouse.name}</strong>, but this
                  material is available elsewhere: <strong>{stockElsewhere}</strong>. Switch
                  warehouse to reserve it, or add it as <strong>Planned</strong>.
                </>
              ) : (
                <>
                  No stock available in any warehouse — this material will be added as{' '}
                  <strong>Planned</strong> and won&apos;t be reserved from inventory.
                </>
              )}
            </p>
          </div>
        )}

        {/* Stock available info */}
        {selectedMaterial && hasStock && (
          <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
            <InfoOutlinedIcon className="text-blue-500 mt-0.5" sx={{ fontSize: 18 }} />
            <p className="text-sm text-blue-700">
              <strong>{maxAvailable}</strong> {selectedMaterial.material.unit || 'units'} available
              in {selectedWarehouse?.warehouse.name}
              {stockElsewhere && ` (plus ${stockElsewhere})`}. You can{' '}
              <strong>Reserve</strong> to hold stock, or <strong>Add as Planned</strong> without
              affecting inventory.
            </p>
          </div>
        )}

        {/* Quantity */}
        {selectedMaterial && selectedWarehouse && (
          <FormGroup label="Quantity" htmlFor="quantity">
            <Input
              id="quantity"
              type="number"
              min="1"
              step="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder={hasStock ? `Available: ${maxAvailable}` : 'Enter quantity'}
              error={quantity !== '' && !isQuantityPositive}
            />
            {quantity !== '' && !isQuantityPositive && (
              <p className="text-xs text-red-500 mt-1">Quantity must be greater than 0</p>
            )}
            {hasStock && quantity !== '' && isQuantityPositive && !isQuantityWithinStock && (
              <p className="text-xs text-amber-600 mt-1">
                Exceeds stock in {selectedWarehouse.warehouse.name} ({maxAvailable}). You can
                still &quot;Add as Planned&quot;.
              </p>
            )}
          </FormGroup>
        )}

        {/* Charge to Client */}
        {isDelegated && selectedMaterial && (
          <div className="flex items-center gap-3">
            <Checkbox
              checked={chargeToClient}
              onChange={(checked) => setChargeToClient(checked)}
              label="Charge to client"
            />
          </div>
        )}

        {/* Price Per Unit (only if charging client) */}
        {chargeToClient && (
          <FormGroup label="Price Per Unit (Client)" htmlFor="pricePerUnit">
            <InputCurrency
              id="pricePerUnit"
              value={pricePerUnit}
              onChange={(val) => setPricePerUnit(val)}
              placeholder="0.00"
            />
          </FormGroup>
        )}

        {/* Cost Summary */}
        {selectedMaterial && quantityNum > 0 && (
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Summary</h4>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">
                Cost ({quantityNum} x ${unitCost.toFixed(2)})
              </span>
              <span className="font-medium text-gray-900">${totalCost.toFixed(2)}</span>
            </div>
            {chargeToClient && priceNum > 0 && (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">
                    Client Charge ({quantityNum} x ${priceNum.toFixed(2)})
                  </span>
                  <span className="font-medium text-gray-900">${totalPrice.toFixed(2)}</span>
                </div>
                <div className="border-t border-gray-200 pt-2 mt-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 font-medium">Profit</span>
                    <span
                      className={`font-semibold ${totalPrice - totalCost >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ${(totalPrice - totalCost).toFixed(2)}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* No warehouses warning */}
        {!warehousesLoading && warehouses.length === 0 && (
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 text-center">
            <p className="text-sm text-amber-700">
              No warehouses configured. Go to Warehouse settings to create one.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AddWarehouseMaterialModal;
